const DEFAULT_EXPORT_WIDTH = (() => {
    const envValue = Number(import.meta.env.VITE_CHART_EXPORT_WIDTH);
    return Number.isFinite(envValue) && envValue > 0 ? envValue : 800;
})();

const DEFAULT_EXPORT_HEIGHT = (() => {
    const envValue = Number(import.meta.env.VITE_CHART_EXPORT_HEIGHT);
    return Number.isFinite(envValue) && envValue > 0 ? envValue : 600;
})();

// Pixel ratio for sharper exports; default 2 keeps text/lines crisp
const DEFAULT_PIXEL_RATIO = (() => {
    const envValue = Number(import.meta.env.VITE_CHART_EXPORT_PIXEL_RATIO);
    return Number.isFinite(envValue) && envValue > 0 ? envValue : 2;
})();

const OFFSCREEN_CANVAS_LEFT = '-100000px';

// Time scale presets for downloads (pick based on span)
export const DOWNLOAD_TIME_SCALES = Object.freeze({
    day: { label: '日', days: 1, maxDays: 1 },
    week: { label: '週', days: 7, maxDays: 7 },
    month: { label: '月', days: 30, maxDays: 30 },
    season: { label: '季', days: 90, maxDays: 90 },
    year: { label: '年', days: 365, maxDays: 365 },
    years: { label: '多年', days: 365 * 3, maxDays: Infinity },
});

// Choose a scale based on span in ms
export function getAutoScale(spanMs) {
    if (!Number.isFinite(spanMs) || spanMs <= 0) return 'day';

    const spanDays = spanMs / (24 * 60 * 60 * 1000);

    if (spanDays <= 1) return 'day';
    if (spanDays <= 7) return 'week';
    if (spanDays <= 31) return 'month';
    if (spanDays <= 92) return 'season';
    if (spanDays <= 365) return 'year';
    return 'years';
}

// Choose a scale based on the span of provided points
export function getAutoScaleFromData(dataPoints) {
    if (!Array.isArray(dataPoints) || dataPoints.length === 0) return 'day';

    const timestamps = dataPoints
        .map((p) => p?.x ?? p?.t ?? p?.timestamp)
        .filter((t) => Number.isFinite(t) || t instanceof Date)
        .map((t) => (t instanceof Date ? t.getTime() : t));

    if (timestamps.length === 0) return 'day';

    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const spanMs = maxTime - minTime;

    return getAutoScale(spanMs);
}

export function getTimeRangeForScale(scale, endDate = new Date()) {
    const scaleConfig = DOWNLOAD_TIME_SCALES[scale];
    if (!scaleConfig) return null;

    const endMs = endDate.getTime();
    const startMs = endMs - scaleConfig.days * 24 * 60 * 60 * 1000;

    return { start: startMs, end: endMs, label: scaleConfig.label };
}

function buildFileName(baseName, suffix) {
    const safeBase = (baseName ? baseName.toString().trim() : '') || 'chart';
    const sanitized = safeBase.replace(/[^\w\u4e00-\u9fa5-]+/g, '_');

    const safeSuffix = suffix != null ? suffix.toString().trim() : '';
    const sanitizedSuffix = safeSuffix
        ? safeSuffix.replace(/[^\w\u4e00-\u9fa5-]+/g, '_')
        : '';
    const suffixLabel = sanitizedSuffix ? `_${sanitizedSuffix}` : '';

    return `${sanitized}${suffixLabel}.png`;
}

function normalizeDimension(value) {
    return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
}

function ensurePositiveDimension(value, fallback) {
    return Math.max(1, value || fallback);
}

function prepareChartForExport(chart, width, height, pixelRatio = 1) {
    const canvas = chart.canvas;
    if (!canvas) return null;

    const options = chart.options || {};

    // Snapshot original font settings
    const originalFonts = {
        title: options.plugins?.title?.font ? { ...options.plugins.title.font } : null,
        legend: options.plugins?.legend?.labels?.font
            ? { ...options.plugins.legend.labels.font }
            : null,
        xTitle: options.scales?.x?.title?.font
            ? { ...options.scales.x.title.font }
            : null,
        yTitle: options.scales?.y?.title?.font
            ? { ...options.scales.y.title.font }
            : null,
        xTicks: options.scales?.x?.ticks?.font
            ? { ...options.scales.x.ticks.font }
            : null,
        yTicks: options.scales?.y?.ticks?.font
            ? { ...options.scales.y.ticks.font }
            : null,
    };

    const originalState = {
        responsive: options.responsive ?? true,
        maintainAspectRatio: options.maintainAspectRatio ?? true,
        animation: options.animation,
        devicePixelRatio: options.devicePixelRatio,
        width: chart.width,
        height: chart.height,
        fonts: originalFonts,
    };
    const originalStyle = {
        width: canvas.style.width,
        height: canvas.style.height,
        position: canvas.style.position,
        left: canvas.style.left,
        top: canvas.style.top,
        right: canvas.style.right,
        bottom: canvas.style.bottom,
        visibility: canvas.style.visibility,
    };

    chart.stop();

    options.responsive = false;
    options.maintainAspectRatio = false;
    options.animation = false;
    // Render with a higher pixel ratio for sharper text/lines
    options.devicePixelRatio = pixelRatio;

    // Use bolder fonts for export clarity
    const boldWeight = 'bold';
    if (options.plugins?.title?.font) {
        options.plugins.title.font.weight = boldWeight;
    }
    if (options.plugins?.legend?.labels) {
        options.plugins.legend.labels.font = {
            ...options.plugins.legend.labels.font,
            weight: boldWeight,
        };
    }
    if (options.scales?.x?.title) {
        const currentSize = options.scales.x.title.font?.size || 12;
        options.scales.x.title.font = {
            ...options.scales.x.title.font,
            weight: boldWeight,
            size: Math.round(currentSize * 1.3), // Scale up 30%
        };
    }
    if (options.scales?.y?.title) {
        const currentSize = options.scales.y.title.font?.size || 12;
        options.scales.y.title.font = {
            ...options.scales.y.title.font,
            weight: boldWeight,
            size: Math.round(currentSize * 1.3), // Scale up 30%
        };
    }
    if (options.scales?.x?.ticks) {
        options.scales.x.ticks.font = {
            ...options.scales.x.ticks.font,
            weight: '600',
        };
    }
    if (options.scales?.y?.ticks) {
        options.scales.y.ticks.font = {
            ...options.scales.y.ticks.font,
            weight: '600',
        };
    }

    canvas.style.visibility = 'hidden';
    canvas.style.position = 'absolute';
    canvas.style.left = OFFSCREEN_CANVAS_LEFT;
    canvas.style.top = '0';
    canvas.style.right = '';
    canvas.style.bottom = '';

    try {
        chart.resize(width, height);
        chart.update('none');
    } catch (error) {
        restoreChartAfterExport(chart, canvas, originalState, originalStyle);
        throw error;
    }

    return () => restoreChartAfterExport(chart, canvas, originalState, originalStyle);
}

function restoreChartAfterExport(chart, canvas, state, style) {
    const options = chart.options || {};
    options.responsive = state.responsive;
    options.maintainAspectRatio = state.maintainAspectRatio;
    options.animation = state.animation;
    // Restore original devicePixelRatio
    if (state.devicePixelRatio !== undefined) {
        options.devicePixelRatio = state.devicePixelRatio;
    } else {
        delete options.devicePixelRatio;
    }

    // Restore font settings
    const fonts = state.fonts || {};
    if (fonts.title && options.plugins?.title) {
        options.plugins.title.font = fonts.title;
    }
    if (fonts.legend && options.plugins?.legend?.labels) {
        options.plugins.legend.labels.font = fonts.legend;
    }
    if (fonts.xTitle && options.scales?.x?.title) {
        options.scales.x.title.font = fonts.xTitle;
    }
    if (fonts.yTitle && options.scales?.y?.title) {
        options.scales.y.title.font = fonts.yTitle;
    }
    if (fonts.xTicks && options.scales?.x?.ticks) {
        options.scales.x.ticks.font = fonts.xTicks;
    }
    if (fonts.yTicks && options.scales?.y?.ticks) {
        options.scales.y.ticks.font = fonts.yTicks;
    }

    canvas.style.position = style.position;
    canvas.style.left = style.left;
    canvas.style.top = style.top;
    canvas.style.right = style.right;
    canvas.style.bottom = style.bottom;
    canvas.style.width = style.width;
    canvas.style.height = style.height;

    const fallbackWidth = ensurePositiveDimension(
        state.width,
        canvas.clientWidth || DEFAULT_EXPORT_WIDTH
    );
    const fallbackHeight = ensurePositiveDimension(
        state.height,
        canvas.clientHeight || DEFAULT_EXPORT_HEIGHT
    );

    if (state.responsive) {
        chart.resize();
    } else {
        chart.resize(fallbackWidth, fallbackHeight);
    }
    chart.update('none');

    canvas.style.visibility = style.visibility;
}

export function downloadChartImage(chartInstance, options = {}) {
    if (!chartInstance?.canvas) return;

    const {
        fileNameBase = 'chart',
        fileNameSuffix = '',
        width: requestedWidth,
        height: requestedHeight,
        backgroundColor = '#ffffff',
        pixelRatio = DEFAULT_PIXEL_RATIO, // Higher ratio improves export quality
    } = options;

    const normalizedWidth = normalizeDimension(requestedWidth);
    const normalizedHeight = normalizeDimension(requestedHeight);

    const aspectSourceCanvas = chartInstance.canvas;
    const fallbackSourceWidth = aspectSourceCanvas?.width || DEFAULT_EXPORT_WIDTH;
    const fallbackSourceHeight = aspectSourceCanvas?.height || DEFAULT_EXPORT_HEIGHT;
    const aspect =
        fallbackSourceHeight > 0
            ? fallbackSourceWidth / fallbackSourceHeight
            : DEFAULT_EXPORT_WIDTH / DEFAULT_EXPORT_HEIGHT;

    const targetWidth =
        normalizedWidth ??
        (normalizedHeight != null
            ? Math.round(normalizedHeight * aspect)
            : DEFAULT_EXPORT_WIDTH);
    const targetHeight =
        normalizedHeight ??
        (normalizedWidth != null
            ? Math.round(normalizedWidth / aspect)
            : DEFAULT_EXPORT_HEIGHT);

    // Apply pixel ratio to boost export resolution (does not change chart size on screen)
    const ratio = Math.max(1, pixelRatio);

    // Render once at the desired ratio
    const restoreChart = prepareChartForExport(
        chartInstance,
        targetWidth,
        targetHeight,
        ratio
    );
    if (!restoreChart) return;

    let imageUrl = null;

    // Canvas size accounts for devicePixelRatio (logical size × pixelRatio)
    const sourceCanvas = chartInstance.canvas;
    const actualWidth = sourceCanvas.width;
    const actualHeight = sourceCanvas.height;

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = actualWidth;
    exportCanvas.height = actualHeight;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
        restoreChart();
        return;
    }

    try {
        if (backgroundColor) {
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, actualWidth, actualHeight);
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw directly; Chart.js already rendered at high resolution
        ctx.drawImage(sourceCanvas, 0, 0);

        imageUrl = exportCanvas.toDataURL('image/png');
    } finally {
        restoreChart();
    }

    if (!imageUrl) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = buildFileName(fileNameBase, fileNameSuffix);
    link.click();
}
